import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { templatesApi, type TemplateModuleSource } from '../api/client'

export function useTemplates() {
  return useQuery({
    queryKey: ['templates'],
    queryFn: templatesApi.list,
  })
}

export function useTemplatesAll() {
  return useQuery({
    queryKey: ['templates', 'all'],
    queryFn: templatesApi.listAll,
  })
}

export function useTemplate(id: string) {
  return useQuery({
    queryKey: ['templates', id],
    queryFn: () => templatesApi.get(id),
    enabled: !!id,
  })
}

export function useCreateTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: templatesApi.create,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['templates'] })
    },
  })
}

export function useUpdateTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: { name?: string; description?: string; is_active?: boolean } }) =>
      templatesApi.update(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['templates'] })
    },
  })
}

export function useDeactivateTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: templatesApi.deactivate,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['templates'] })
    },
  })
}

export function useDeleteTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: templatesApi.delete,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['templates'] })
    },
  })
}

export function useAddTemplateModule() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: { source?: TemplateModuleSource; github_url?: string; branch?: string; subdirectory?: string; archive_sha?: string; module_name: string; description?: string } }) =>
      templatesApi.addModule(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['templates'] })
    },
  })
}

export function useCreateTemplateFromInstance() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ instanceId, body }: { instanceId: string; body: { name: string; description?: string } }) =>
      templatesApi.createFromInstance(instanceId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['templates'] })
    },
  })
}

export function useRemoveTemplateModule() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ templateId, moduleId }: { templateId: string; moduleId: string }) =>
      templatesApi.removeModule(templateId, moduleId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['templates'] })
    },
  })
}

export function useSetDefault() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => templatesApi.setDefault(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['templates'] })
    },
  })
}

export function useValidateTemplate() {
  return useMutation({
    mutationFn: (id: string) => templatesApi.validate(id),
  })
}

export function useTemplateUsage(id: string, enabled: boolean = true) {
  return useQuery({
    queryKey: ['templates', id, 'usage'],
    queryFn: () => templatesApi.usage(id),
    enabled: !!id && enabled,
    staleTime: 30_000,
  })
}

/** Freeze a template's git-referenced modules into stored archives. */
export function useFreezeTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => templatesApi.freeze(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['templates'] })
    },
  })
}
